import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { CorrectOption, QuizQuestion } from '../../../services/quizContentAdminService';
import { Column, FieldRun, OPTION_LETTERS, resolveField, staleRule } from './shared';
import InlineEditable, { ERROR_BOX, ERRBTN, FOCUS, STATUS } from './InlineEditable';
import { EditLine, useEditCoordinator } from './editCoordinator';
import { FIELD_LABELS, QUIZ_REVIEW_EDIT_COPY as EDIT } from './editCopy';
import { QuestionPatch } from './contentPatch';
import { ItemPendingChip, useLessonId, useReviewEdit } from './reviewEdit';
import { SaveSpinner } from '../curriculum/SaveStatus';
import AUTO_SAVE_COPY from '../curriculum/autoSaveCopy';
import MicButton from './voiceEdit/MicButton';
import QuestionEditThread from './voiceEdit/QuestionEditThread';
import { isOptionFilled } from '../../../utils/mcOptions';

export interface QuestionCardProps {
  q: QuizQuestion;
  index: number;
  column: Column;
}

const LETTERS: readonly CorrectOption[] = ['a', 'b', 'c', 'd'];
const OPTION_FIELDS = ['option_a', 'option_b', 'option_c', 'option_d'] as const;

/** How long "Correct answer is now B · Undo" stays (design NOTES §7). */
export const UNDO_WINDOW_MS = 6000;

/** The 28 px bullet slot every choice row has in edit chrome (NOTES §7). */
const SLOT = 'shrink-0 -ml-[7px] -my-0.5 grid place-items-center w-7 h-7';

/**
 * A question.
 *
 * `Q{n}.`, the `A) B) C) D)` letters and the ✅ are CHROME and stay English in
 * every mode (spec §Page chrome). `correct_option` is not translatable, so the
 * ✅ sits on the same index in both columns.
 *
 * feat-299: inside the page (`ReviewEditContext` present) the ENGLISH cell's
 * text is click-to-edit and each choice's bullet is its mark-correct control;
 * a translated cell keeps today's rendering with a plain dot in the same 28 px
 * slot so the two columns stay aligned. Outside the page (no context) the card
 * renders exactly as feat-298 shipped it.
 */
const QuestionCard: React.FC<QuestionCardProps> = ({ q, index, column }) => {
  const api = useReviewEdit();
  const lessonId = useLessonId();
  const coordinator = useEditCoordinator();
  const chrome = api !== null;
  const editable = chrome && column === null;

  // --- mark correct (spec §2, NOTES §7) ----------------------------------------------
  const [optimistic, setOptimistic] = useState<CorrectOption | null>(null);
  const [undo, setUndo] = useState<CorrectOption | null>(null);
  const [markError, setMarkError] = useState<CorrectOption | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    if (!undo) return undefined;
    const t = window.setTimeout(() => setUndo(null), UNDO_WINDOW_MS);
    return () => window.clearTimeout(t);
  }, [undo]);

  const markCorrect = async (letter: CorrectOption, offerUndo: boolean) => {
    if (!api || optimistic) return;
    // An open field of this page commits first, the same as opening another field.
    coordinator?.commitOpen();
    const previous = q.correct_option;
    setOptimistic(letter);
    setMarkError(null);
    setUndo(null);
    try {
      await api.saveQuestion(lessonId, q.id, { correct_option: letter });
      if (!mounted.current) return;
      setOptimistic(null);
      if (offerUndo) setUndo(previous);
    } catch {
      if (!mounted.current) return;
      setOptimistic(null);
      setMarkError(letter);
    }
  };

  const shownCorrect = editable && optimistic ? optimistic : q.correct_option;
  const correctIdx = LETTERS.indexOf(shownCorrect);
  const lang = column ?? '';
  const text = resolveField(q.question_text, q.translations, column, 'question_text');
  const options = OPTION_FIELDS.map((f) => resolveField(q[f], q.translations, column, f));
  // feat-346: a 2–3 option question leaves C/D blank — those rows are not
  // rendered, in either column. Keyed off the ENGLISH option, so a stale
  // translation of a now-blank option never shows, and both columns keep the
  // same rows. Indices are kept, so letters and the ✅ stay on their option.
  const shown = OPTION_FIELDS.map((_, i) => i).filter((i) => isOptionFilled(q[OPTION_FIELDS[i]]));
  const explain = resolveField(q.explanation?.trim(), q.translations, column, 'explanation');
  const praise = resolveField(
    q.correct_explanation?.trim(),
    q.translations,
    column,
    'correct_explanation',
  );

  if (!chrome) {
    // feat-298 rendering, unchanged.
    return (
      <div className="group/q">
        {/* feat-300: the AI-edit controls sit top-right of the ENGLISH stem only, so a
            translated cell keeps its feat-284 DOM exactly (NOTES §2, §7). */}
        {column === null ? (
          <div className="font-semibold text-ink mb-1 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              Q{index}. <FieldRun f={text} language={lang} />
            </div>
            <MicButton q={q} index={index} column={column} />
          </div>
        ) : (
          <div className="font-semibold text-ink mb-1">
            Q{index}. <FieldRun f={text} language={lang} />
          </div>
        )}
        <ul className="list-disc pl-6 space-y-0.5 text-ink">
          {shown.map((i) => (
            <li key={i}>
              {OPTION_LETTERS[i]}) <FieldRun f={options[i]} language={lang} />
              {i === correctIdx && <span className="ml-2">✅</span>}
            </li>
          ))}
        </ul>
        {explain.text && (
          <div className={`mt-1 text-inkMuted italic text-sm pl-1${staleRule(explain)}`}>
            <span className={explain.marker === 'stale' ? 'pl-2' : undefined}>
              💡 <FieldRun f={explain} language={lang} className="text-ink/80" />
            </span>
          </div>
        )}
        {praise.text && (
          <div className={`mt-1 text-emerald-700 italic text-sm pl-1${staleRule(praise)}`}>
            <span className={praise.marker === 'stale' ? 'pl-2' : undefined}>
              🎉 <FieldRun f={praise} language={lang} />
            </span>
          </div>
        )}
        <QuestionEditThread q={q} index={index} column={column} />
      </div>
    );
  }

  const save = (field: string) => (next: string) =>
    api.saveQuestion(lessonId, q.id, { [field]: next } as QuestionPatch);

  const bullet = (i: number) => {
    const L = OPTION_LETTERS[i];
    if (!editable) {
      return (
        <span className={SLOT} aria-hidden>
          <span className="block w-1.5 h-1.5 rounded-full bg-ink" />
        </span>
      );
    }
    if (i === correctIdx) {
      return (
        <span role="img" aria-label={EDIT.isCorrect(L)} className={SLOT}>
          {optimistic ? (
            <span className="text-success-on">
              <SaveSpinner size={14} />
            </span>
          ) : (
            <span className="grid place-items-center w-3.5 h-3.5 rounded-full bg-success-on text-white">
              <Check className="w-2.5 h-2.5" strokeWidth={4} aria-hidden />
            </span>
          )}
        </span>
      );
    }
    return (
      <button
        type="button"
        aria-label={EDIT.markCorrect(L)}
        title={EDIT.markCorrect(L)}
        disabled={optimistic !== null}
        onClick={() => void markCorrect(LETTERS[i], true)}
        className={`group/mc ${SLOT} rounded-full hover:bg-black/[0.05] ${FOCUS}`}
      >
        <span className="block w-3.5 h-3.5 rounded-full border-2 border-inkMuted/45 group-hover/mc:border-success-on transition-colors" />
      </button>
    );
  };

  return (
    <div className="group/q">
      {/* feat-300: the English stem row is `flex` — the click-to-edit line takes
          the rest of the row and the mic sits after it. A translated cell keeps
          its plain feat-284 DOM (no edit affordance, no mic). */}
      {editable ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <EditLine className="font-semibold text-ink mb-1" prefix={`Q${index}.`}>
              <InlineEditable
                value={q.question_text ?? ''}
                field="question_text"
                label={FIELD_LABELS.question_text()}
                required
                onSave={save('question_text')}
              />
              <ItemPendingChip entity="question_item" id={q.id} />
            </EditLine>
          </div>
          <MicButton q={q} index={index} column={column} />
        </div>
      ) : (
        <div className="font-semibold text-ink mb-1">
          Q{index}. <FieldRun f={text} language={lang} />
        </div>
      )}
      <ul className="text-ink pl-3">
        {shown.map((i) => {
          const opt = options[i];
          const L = OPTION_LETTERS[i];
          const correct = i === correctIdx;
          return (
            <li key={i} className="flex items-start gap-1">
              {bullet(i)}
              <span className="flex-1 min-w-0 pt-0.5">
                {editable ? (
                  <EditLine as="span" prefix={`${L})`}>
                    <InlineEditable
                      value={q[OPTION_FIELDS[i]] ?? ''}
                      field={OPTION_FIELDS[i]}
                      label={FIELD_LABELS.option(L)}
                      required
                      onSave={save(OPTION_FIELDS[i])}
                    />
                  </EditLine>
                ) : (
                  <>
                    {L}) <FieldRun f={opt} language={lang} />
                  </>
                )}
                {correct && <span className="ml-2">✅</span>}
                {editable && correct && undo && (
                  <span className={STATUS} aria-live="polite" data-testid="mark-correct-status">
                    <span aria-hidden>✓</span> {EDIT.correctNowIs(L)} ·{' '}
                    <button
                      type="button"
                      className={`${ERRBTN} !text-inkMuted hover:!text-ink`}
                      onClick={() => void markCorrect(undo, false)}
                    >
                      {EDIT.undo}
                    </button>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {editable && markError && (
        <div role="alert" className={ERROR_BOX} data-testid="mark-correct-error">
          <span className="font-semibold">
            <span aria-hidden>⚠</span> {EDIT.couldNotChangeCorrect}
          </span>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <button
              type="button"
              className={ERRBTN}
              onClick={() => void markCorrect(markError, true)}
            >
              {AUTO_SAVE_COPY.tryAgain}
            </button>
            <button type="button" className={ERRBTN} onClick={() => setMarkError(null)}>
              {EDIT.dismiss}
            </button>
          </div>
        </div>
      )}
      {editable ? (
        <>
          <EditLine className="mt-1 text-inkMuted italic text-sm pl-1" prefix="💡">
            <InlineEditable
              value={q.explanation ?? ''}
              field="explanation"
              label={FIELD_LABELS.explanation()}
              multiline
              placeholder={EDIT.placeholderExplanation}
              className="text-ink/80"
              onSave={save('explanation')}
            />
          </EditLine>
          <EditLine className="mt-1 text-emerald-700 italic text-sm pl-1" prefix="🎉">
            <InlineEditable
              value={q.correct_explanation ?? ''}
              field="correct_explanation"
              label={FIELD_LABELS.correct_explanation()}
              multiline
              placeholder={EDIT.placeholderCorrectExplanation}
              onSave={save('correct_explanation')}
            />
          </EditLine>
        </>
      ) : (
        <>
          {explain.text && (
            <div className={`mt-1 text-inkMuted italic text-sm pl-1${staleRule(explain)}`}>
              <span className={explain.marker === 'stale' ? 'pl-2' : undefined}>
                💡 <FieldRun f={explain} language={lang} className="text-ink/80" />
              </span>
            </div>
          )}
          {praise.text && (
            <div className={`mt-1 text-emerald-700 italic text-sm pl-1${staleRule(praise)}`}>
              <span className={praise.marker === 'stale' ? 'pl-2' : undefined}>
                🎉 <FieldRun f={praise} language={lang} />
              </span>
            </div>
          )}
        </>
      )}
      <QuestionEditThread q={q} index={index} column={column} />
    </div>
  );
};

export default QuestionCard;
